import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
    Trophy, Clock, MapPin, Play, CheckCircle, 
    ArrowLeft, RefreshCw, Users, Calendar
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LiveMatchesPage = () => {
    const { token } = useAuth();
    const [liveMatches, setLiveMatches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const fetchLiveMatches = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/matches/live`);
            setLiveMatches(response.data);
        } catch (error) {
            console.error('Failed to fetch live matches:', error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchLiveMatches();
        // Poll every 30 seconds for updates
        const interval = setInterval(fetchLiveMatches, 30000);
        return () => clearInterval(interval);
    }, []);

    const handleRefresh = () => {
        setRefreshing(true);
        fetchLiveMatches();
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#0a1628] flex items-center justify-center">
                <RefreshCw className="w-8 h-8 text-primary animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0a1628]">
            {/* Header */}
            <div className="bg-[#0d2847] border-b border-white/5">
                <div className="max-w-7xl mx-auto px-4 py-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <Link to="/" className="text-muted-foreground hover:text-white">
                                <ArrowLeft className="w-5 h-5" />
                            </Link>
                            <div>
                                <h1 className="font-heading font-bold text-2xl uppercase tracking-tight text-white flex items-center gap-2">
                                    <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                                    Live Matches
                                </h1>
                                <p className="text-sm text-muted-foreground">Real-time scores and updates</p>
                            </div>
                        </div>
                        <Button 
                            onClick={handleRefresh} 
                            variant="outline" 
                            className="border-white/10"
                            disabled={refreshing}
                            data-testid="refresh-matches-btn"
                        >
                            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                            Refresh
                        </Button>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-7xl mx-auto px-4 py-8">
                {liveMatches.length === 0 ? (
                    <div className="text-center py-16">
                        <Trophy className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                        <h2 className="text-xl font-bold text-white mb-2">No Live Matches</h2>
                        <p className="text-muted-foreground">Check back later for live match updates</p>
                    </div>
                ) : (
                    <div className="grid gap-6">
                        {liveMatches.map((match) => (
                            <LiveMatchCard key={match.id} match={match} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

const LiveMatchCard = ({ match }) => {
    return (
        <div className="bg-[#0d2847] border border-white/5 overflow-hidden" data-testid={`live-match-${match.id}`}>
            {/* Match Header */}
            <div className="bg-[#0a1628] px-6 py-3 flex items-center justify-between border-b border-white/5">
                <div className="flex items-center gap-3">
                    <span className="text-xs bg-red-500/20 text-red-400 px-2 py-1 flex items-center gap-1">
                        <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                        LIVE
                    </span>
                    <span className="text-sm text-primary">{match.tournament?.sport || match.sport}</span>
                </div>
                <div className="text-xs text-muted-foreground flex items-center gap-4">
                    <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {match.venue || match.tournament?.location}
                    </span>
                    <span>{match.round}</span>
                </div>
            </div>

            {/* Score Display */}
            <div className="p-6">
                <div className="flex items-center justify-between">
                    {/* Team 1 */}
                    <div className="flex-1 text-center">
                        <div className="text-lg font-bold text-white mb-2">{match.team1}</div>
                    </div>

                    {/* Score */}
                    <div className="px-8">
                        <div className="flex items-center gap-4">
                            <span className="text-4xl font-bold text-white">{match.score1 || 0}</span>
                            <span className="text-2xl text-muted-foreground">-</span>
                            <span className="text-4xl font-bold text-white">{match.score2 || 0}</span>
                        </div>
                    </div>

                    {/* Team 2 */}
                    <div className="flex-1 text-center">
                        <div className="text-lg font-bold text-white mb-2">{match.team2}</div>
                    </div>
                </div>

                {/* Tournament Info */}
                <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-center gap-2 text-sm text-muted-foreground">
                    <Trophy className="w-4 h-4" />
                    {match.tournament?.name || 'Tournament'}
                </div>
            </div>

            {/* Match Events */}
            {match.match_events && match.match_events.length > 0 && (
                <div className="bg-[#0a1628] px-6 py-4 border-t border-white/5">
                    <div className="text-xs text-muted-foreground mb-2">Recent Events</div>
                    <div className="space-y-2">
                        {match.match_events.slice(-3).reverse().map((event, i) => (
                            <div key={i} className="flex items-center gap-2 text-sm">
                                <span className="text-primary">{event.minute}'</span>
                                <span className="text-white">{event.event_type}</span>
                                <span className="text-muted-foreground">- {event.team}</span>
                                {event.player && <span className="text-muted-foreground">({event.player})</span>}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default LiveMatchesPage;
